import { scopedKey, currentScope } from './scope'

/**
 * The only way anything in this app touches localStorage (T-020).
 *
 * Several shops share one deployment, so an un-namespaced key is a route for
 * one shop's data to appear inside another shop's session. Nothing errors when
 * that happens — the wrong numbers simply appear, and they look plausible.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY *NOT* SCOPED, AND WHY
 *
 * Two categories bypass this wrapper on purpose. Both are listed in the guard
 * test so the exemption is a decision on the record rather than an oversight.
 *
 *   The session itself — token and userDetails. There is a chicken-and-egg
 *   here that cannot be designed away: the scope is derived FROM the user
 *   record, so the user record cannot live inside the scope. These are cleared
 *   wholesale on logout instead.
 *
 *   The theme. It belongs to the screen someone is sitting at, not to their
 *   account: the counter PC by the window wants light and the back office at
 *   night wants dark, whoever is on shift. It also has to be readable before
 *   anyone has logged in.
 *
 * Everything else — preferences, cached lists, drafts — is scoped.
 * ---------------------------------------------------------------------------
 */

export function getItem(name, fallback = null) {
    try {
        const raw = localStorage.getItem(scopedKey(name))
        return raw === null ? fallback : JSON.parse(raw)
    } catch (e) {
        // Corrupt JSON, a manual edit, or storage that refuses to be read.
        // A cache that cannot be read is a cache miss, not an error.
        return fallback
    }
}

export function setItem(name, value) {
    try {
        localStorage.setItem(scopedKey(name), JSON.stringify(value))
        return true
    } catch (e) {
        // A full quota must not interrupt someone mid-invoice. The work is
        // still on screen and still saveable; only the convenience is lost.
        return false
    }
}

export function removeItem(name) {
    try {
        localStorage.removeItem(scopedKey(name))
        return true
    } catch (e) {
        return false
    }
}

/**
 * Everything belonging to the user who is logging out — and nothing belonging
 * to anyone else.
 *
 * This is the difference between a shared counter PC being safe and not. A
 * plain localStorage.clear() also takes the theme and any other machine-level
 * preference; worse, it gives no protection at all in the direction that
 * matters, because the risk is not leftover data surviving — it is leftover
 * data being READ by the next person, which only key scoping prevents.
 */
/**
 * Everything cached for one SHOP, for every user of this browser.
 *
 * Called when a shop is revoked — the user was removed from a branch while they
 * were working in it. clearScope() cannot do this job: it clears the scope that
 * is CURRENT, and by the time revocation is noticed the active shop has already
 * moved on, so the revoked shop's keys would be exactly the ones left behind.
 *
 * The drafts are why this matters. They are keyed by shop, so a half-finished
 * invoice for a branch the user no longer works in stays on the machine and can
 * resurface under another shop's name — which storage/scope.js calls, in its own
 * header, "the single worst failure mode this system has", and it is silent.
 *
 * @returns {number} how many keys were removed
 */
export function purgeShopScope(shopId) {
    if (shopId === null || shopId === undefined || shopId === '') return 0

    try {
        const prefix = `shop:${String(shopId)}:`
        // Collect first — see clearScope for why iterating and removing at the
        // same time skips every other key.
        const doomed = []
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i)
            if (key && key.startsWith(prefix)) doomed.push(key)
        }
        doomed.forEach((key) => localStorage.removeItem(key))
        return doomed.length
    } catch (e) {
        return 0
    }
}

export function clearScope() {
    try {
        const prefix = `${currentScope()}:`
        // Collect first: removing while iterating localStorage's index shifts
        // the remaining entries and silently skips every other key.
        const doomed = []
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i)
            if (key && key.startsWith(prefix)) doomed.push(key)
        }
        doomed.forEach((key) => localStorage.removeItem(key))
        return doomed.length
    } catch (e) {
        return 0
    }
}
